import { BackLink } from '@mdvip/shared/components/blocks/BackLink';
import type { IconName } from '@mdvip/shared/components/blocks/Icon';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { WellbeingSubnav } from '@/components/WellbeingSubnav';

interface NutritionQuickLink {
  to: string;
  icon: IconName;
  labelKey: string;
}

const QUICK_LINKS = [
  { to: '/resources/nutrition/articles', icon: 'bookOpen', labelKey: 'pages.nutrition.quickLinks.articles.label' },
  { to: '/resources/nutrition/recipes', icon: 'utensils', labelKey: 'pages.nutrition.quickLinks.recipes.label' },
  { to: '/resources/nutrition/meal-plans', icon: 'calendar', labelKey: 'pages.nutrition.quickLinks.mealPlans.label' },
  { to: '/bookmarks', icon: 'bookmark', labelKey: 'pages.nutrition.quickLinks.bookmarks.label' },
] as const satisfies readonly NutritionQuickLink[];

// Matches the focus-ring recipe `ResourcesPage` and `Navigation` already define locally for their
// own hand-styled links.
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const CARD_CLASS = `group flex min-h-11 items-center gap-4 rounded-xl border bg-card px-4 py-4 shadow-sm transition-[box-shadow,border-color] duration-200 ease-out hover:border-hairline-brand hover:shadow-md ${FOCUS_RING}`;

function QuickLinkCard({ item }: { item: (typeof QUICK_LINKS)[number] }) {
  const { t } = useTranslation('members');

  return (
    <Link to={item.to} className={CARD_CLASS}>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-[8px] bg-secondary text-secondary-foreground">
        <Icon name={item.icon} size="sm" />
      </span>
      <span className="min-w-0 flex-1 text-sm font-semibold text-foreground">{t(item.labelKey)}</span>
      <Icon
        name="chevronRight"
        size="xs"
        className="shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-safe:group-hover:translate-x-0.5"
      />
    </Link>
  );
}

/**
 * The Nutrition hub. This is the head of the page only: the featured carousel and the
 * Articles/Recipes/Meal Plans content bands land in later stories.
 */
export function NutritionPage() {
  const { t } = useTranslation('members');

  return (
    <div className="flex flex-col gap-2">
      <BackLink href="/resources" label={t('pages.nutrition.backToResources')} />
      <WellbeingSubnav active="nutrition" className="pt-1" />
      <h1 className="pt-4 text-h1 font-semibold text-foreground">{t('pages.nutrition.title')}</h1>
      <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">{t('pages.nutrition.subtitle')}</p>
      <div className="grid grid-cols-1 gap-2 pt-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-4 lg:grid-cols-4">
        {QUICK_LINKS.map((item) => (
          <QuickLinkCard key={item.to} item={item} />
        ))}
      </div>
    </div>
  );
}
